import { useMemo, useState } from "react";
import { usePortfolio, type PortfolioProject } from "@/hooks/usePortfolio";
import { useLandingContent } from "@/lib/landing/LandingContentProvider";
import { resolveShowcase } from "@/lib/landing/showcase";
function transformed(url: string, width: number) {
  return `${url.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/")}?width=${width}&quality=70&resize=contain`;
}
function WorkImage({
  project,
  name,
}: {
  project: PortfolioProject;
  name: string;
}) {
  const [fallback, setFallback] = useState(false);
  const src = project.image_url || "";
  return (
    <img
      src={fallback ? src : transformed(src, 960)}
      srcSet={
        fallback
          ? undefined
          : `${transformed(src, 480)} 480w, ${transformed(src, 960)} 960w`
      }
      sizes="(max-width: 1023px) 50vw, 33vw"
      width="960"
      height="540"
      loading="lazy"
      decoding="async"
      alt={`${name} website screenshot`}
      onError={() => setFallback(true)}
      className="h-full w-full object-cover object-top"
    />
  );
}
export default function LandingRecentWork() {
  const { data, isFetched } = usePortfolio();
  const { content, definition } = useLandingContent();
  const listed = content.work.items;
  const items = useMemo(
    () => resolveShowcase(definition.showcase, listed, data?.projects ?? [], data?.categories ?? []),
    [data, definition, listed],
  );
  if (isFetched && !items.length) return null;
  return (
    <section
      aria-labelledby="recent-work-title"
      className="bg-landing-light py-14 text-landing-ink sm:py-24"
    >
      <div className="landing-container">
        <p className="landing-eyebrow text-landing-red">{content.work.eyebrow}</p>
        <h2 id="recent-work-title" className="landing-section-title mt-3">
          {content.work.title}
        </h2>
        <p className="mt-2 text-[18px] text-landing-light-body">
          {content.work.subtitle}
        </p>
        <div className="mt-10 grid grid-cols-2 gap-x-3.5 gap-y-5 sm:gap-x-7 sm:gap-y-8 lg:grid-cols-3">
          {!isFetched &&
            Array.from({ length: listed.length || 6 }, (_, index) => (
              <article key={index} aria-hidden="true">
                <div className="overflow-hidden rounded-xl border border-landing-frame-border bg-landing-soft shadow-landing-frame">
                  <div className="h-[30px] bg-landing-frame-bar" />
                  <div className="aspect-video" />
                </div>
                <div className="mt-3 h-[27px]" />
                <div className="mt-0.5 h-[22px]" />
              </article>
            ))}
          {items.map(({ project, name, label }) => (
            <article key={project.id} className="min-w-0">
              <div className="overflow-hidden rounded-xl border border-landing-frame-border bg-landing-soft shadow-landing-frame">
                <div className="flex h-[30px] items-center gap-1.5 border-b border-landing-frame-border bg-landing-frame-bar px-3">
                  <i className="h-[9px] w-[9px] rounded-full bg-landing-dot" />
                  <i className="h-[9px] w-[9px] rounded-full bg-landing-dot" />
                  <i className="h-[9px] w-[9px] rounded-full bg-landing-dot" />
                </div>
                <div className="aspect-video">
                  {project.image_url && (
                    <WorkImage project={project} name={name} />
                  )}
                </div>
              </div>
              <h3 className="mt-3 font-display text-[15px] font-semibold sm:text-[18px]">
                {name}
              </h3>
              <p className="mt-0.5 text-[13px] text-landing-type-label sm:text-[14.5px]">
                {label}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
